import Vue from 'vue'
import {SUPPORT_LOAD_OLDER, SUPPORT_READ, SUPPORT_SEND} from '@/store/actions.type'
import RestService from "@/common/rest.service";
import {DATA_REFRESH, LOGOUT, NOTIFICATION_SUPPORT, SUPPORT_MESSAGE, SUPPORT_MESSAGES_OLDER, SUPPORT_UNREAD, USER} from '@/store/mutations.type'
import {COMPANY_NAME} from "@/common/config";

// One page of chat history, matching the server's default. The initial load gets the NEWEST
// page (the service orders DESC and flips it back to chronological); older pages are pulled in
// by SUPPORT_LOAD_OLDER as the client scrolls up (Trello L5MLq96J - the platform showed only
// "records from today" on long chats because nothing ever asked for a second page).
const PAGE = 50

const state = {
    supportMessages: [],
    supportUnread: false,
    supportHasOlder: true,
    supportOlderLoading: false,
    // Bumped on every prepend so the view can tell "older history arrived" (keep the scroll
    // anchored) apart from "a new message arrived" (scroll to the bottom).
    supportPrependTick: 0
}

const getters = {
    supportMessages() {
        return state.supportMessages
    },

    supportUnread() {
        return state.supportUnread
    },

    supportHasOlder() {
        return state.supportHasOlder
    },

    supportOlderLoading() {
        return state.supportOlderLoading
    },

    supportPrependTick() {
        return state.supportPrependTick
    }
}

const actions = {
    [SUPPORT_SEND](context, text) {
        RestService.post('/support/messages', {text})
    },

    [SUPPORT_READ]() {
        RestService.post('/support/messages/ack', {})

        this.commit(SUPPORT_UNREAD, false)
    },

    [SUPPORT_LOAD_OLDER]() {
        if (state.supportOlderLoading || !state.supportHasOlder) {
            return Promise.resolve(0)
        }

        Vue.set(state, 'supportOlderLoading', true)
        // RestService.get never rejects (errors only hit the global handler), so a failed
        // request would leave the flag stuck and paging dead. Clear it after a grace period.
        const unstick = setTimeout(() => Vue.set(state, 'supportOlderLoading', false), 15000)

        // Offsets are anchored to the NEWEST message. Every message that arrived over the
        // socket since login was appended to this array AND shifted the server-side window by
        // one - so the number of messages held here IS the offset of the next older page.
        return RestService.get('/support/messages', {offset: state.supportMessages.length, count: PAGE})
            .then(messages => {
                messages = messages || []

                this.commit(SUPPORT_MESSAGES_OLDER, messages)

                if (messages.length < PAGE) {
                    Vue.set(state, 'supportHasOlder', false)
                }

                return messages.length
            })
            .finally(() => {
                clearTimeout(unstick)
                Vue.set(state, 'supportOlderLoading', false)
            })
    }
}

const mutations = {
    [USER]() {
        RestService.get('/support/messages')
            .then(messages => {
                Vue.set(state, 'supportMessages', [])
                Vue.set(state, 'supportHasOlder', (messages || []).length >= PAGE)

                for(let n in messages) {
                    this.commit(SUPPORT_MESSAGE, messages[n])
                }
            })
    },

    [SUPPORT_MESSAGE](context, message) {
        if (!message.time_read && message.manager_id > 0 && this.getters.user.enable_chat) {
            this.commit(NOTIFICATION_SUPPORT,
                {
                    title:`New message from ${COMPANY_NAME}`,
                    text: message.text,
                }
            )
            this.commit(SUPPORT_UNREAD, true)
        }

        state.supportMessages.push(message)
    },

    [SUPPORT_MESSAGES_OLDER](context, messages) {
        // The page arrives oldest-first within itself (the service flips its DESC page back to
        // chronological), so it prepends as-is. Rows already held are dropped by id: a message
        // landing mid-scroll shifts every server-side offset by one and the same row comes back
        // in two pages - the trap the CRM fix for this card documented.
        const have = new Set(state.supportMessages.map(m => m.id))
        const older = (messages || []).filter(m => !have.has(m.id))

        if (!older.length) {
            return
        }

        state.supportMessages.unshift(...older)
        Vue.set(state, 'supportPrependTick', state.supportPrependTick + 1)
    },

    [DATA_REFRESH]() {
        state.supportMessages = []

        RestService.get('/support/messages')
            .then(messages => {
                Vue.set(state, 'supportHasOlder', (messages || []).length >= PAGE)

                for(let n in messages) {
                    this.commit(SUPPORT_MESSAGE, messages[n])
                }
            })
    },

    [SUPPORT_UNREAD](context, val) {
        state.supportUnread = val
    },

    [LOGOUT]() {
        Vue.set(state, 'supportMessages', [])
        Vue.set(state, 'supportHasOlder', true)
        Vue.set(state, 'supportOlderLoading', false)
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}
